import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, Package, ShieldCheck, Clock, ShieldOff, Plus, PencilLine } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { Spinner } from "@/components/ww/Misc";
import { ProductCard } from "./Wallet";

const Stat = ({ icon: I, label, value, testid }) => (
  <div className="rounded-[22px] border border-ww-border bg-white p-5" data-testid={testid}>
    <div className="flex items-center justify-between text-sm text-ww-muted">{label}<I className="h-4 w-4 text-ww-emerald" /></div>
    <p className="mt-2 font-display text-3xl font-semibold text-ww-navy">{value}</p>
  </div>
);

export default function SharedWallet() {
  const { ownerId } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    api("get", `/family/shared/${ownerId}/products`).then(setData).catch((e) => { toast.error(errMsg(e), { id: "shared-wallet-error" }); navigate("/wallet"); });
  }, [api, ownerId, navigate]);
  if (!data) return <Spinner />;
  const m = data.metrics;
  return (
    <main className="mx-auto max-w-7xl px-5 py-12 lg:px-8" data-testid="shared-wallet-page">
      <Link to="/wallet" className="flex items-center gap-1.5 text-sm font-semibold text-ww-muted hover:text-ww-emerald" data-testid="shared-back-button"><ArrowLeft className="h-4 w-4" />My Wallet</Link>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-ww-navy sm:text-5xl" data-testid="shared-wallet-title">{data.owner_name}'s Wallet</h1>
          <p className="mt-2 text-ww-muted">Household wallet shared with you, sorted by nearest expiry.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data.can_edit ? <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-sky-50 px-3 py-1.5 text-xs font-bold uppercase text-sky-700" data-testid="shared-editor-badge"><PencilLine className="h-3.5 w-3.5" />Can add & edit</span>
            : <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-ww-mint px-3 py-1.5 text-xs font-bold uppercase text-ww-emerald" data-testid="shared-view-only-badge"><Eye className="h-3.5 w-3.5" />View only</span>}
          {data.can_edit && <Link to={`/shared/${ownerId}/add`} className="ww-btn-primary" data-testid="shared-add-product-button"><Plus className="h-4 w-4" />Add Product</Link>}
        </div>
      </div>
      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={Package} label="Products" value={m.saved} testid="shared-metric-saved" />
        <Stat icon={ShieldCheck} label="Active" value={m.active} testid="shared-metric-active" />
        <Stat icon={Clock} label="Expiring Soon" value={m.expiring} testid="shared-metric-expiring" />
        <Stat icon={ShieldOff} label="Expired" value={m.expired} testid="shared-metric-expired" />
      </div>
      {data.products.length === 0 ? <p className="py-16 text-center text-ww-muted" data-testid="shared-wallet-empty">No products in this wallet yet.</p> : (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="shared-product-grid">
          {data.products.map((p) => <ProductCard key={p.id} p={p} to={`/shared/${ownerId}/products/${p.id}`} />)}
        </div>
      )}
    </main>
  );
}
